<template>
  <aside class="sidebar" id="appsidebar" :class="{ open: shell.navOpen }">
    <a class="slogo" :href="assetUrl('/')"><img :src="logoSrc" alt="SoaRank"></a>
    <nav class="snav" :aria-label="t('nav_dashboard')">
      <NavItem view="maya" extra-class="maya">
        <img class="navav" :src="mayaSrc" alt="" aria-hidden="true">
        <span class="navlbl">
          <span class="nn">Maya</span>
          <span class="nr">{{ t("maya_yourcmo") }}</span>
        </span>
        <!-- GEO_EXEC_R19: unread proactive messages; zero while the switch is off (never polled) -->
        <span v-if="mayaUnread.count" class="nbadge" aria-label="Unread messages from Maya">{{ mayaUnread.count }}</span>
      </NavItem>

      <div class="sgroup">{{ t("grp_account") }}</div>
      <NavItem view="overview">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
        <span>{{ t("nav_dashboard") }}</span>
      </NavItem>
      <button type="button" id="nav-add" class="snav-item snav-add" @click="addBrand">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
        <span>{{ t("nav_add") }}</span>
      </button>

      <div class="sgroup">{{ t("grp_this_brand") }}</div>
      <NavItem view="reports">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M14 3v5h5" />
          <path d="M6 3h9l5 5v13H6z" />
          <path d="M9 13h6M9 17h4" />
        </svg>
        <span>{{ t("nav_reports") }}</span>
      </NavItem>
      <NavItem view="plan">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="17" rx="2" />
          <path d="M8 2v4M16 2v4M3 9h18" />
          <path d="M9 14l2 2 4-4" />
        </svg>
        <span>{{ t("nav_plan") }}</span>
      </NavItem>
      <NavItem view="sites">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
        </svg>
        <span>{{ t("nav_sites") }}</span>
      </NavItem>
      <!-- GEO_EXEC_R14: hidden entirely while the switch is off (the list 404s, and a nav item that leads to a 404 is worse than no nav item) -->
      <NavItem v-if="distribution.enabled" view="distribution">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M21 3L10.5 13.5" />
          <path d="M21 3l-6.8 18-3.7-7.5L3 9.8z" />
        </svg>
        <span>{{ t("nav_distribution") }}</span>
      </NavItem>
      <NavItem view="files">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6" />
        </svg>
        <span>{{ t("nav_files") }}</span>
      </NavItem>
      <NavItem view="profile">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M4 20v-1a5 5 0 0 1 5-5h2" />
          <circle cx="10" cy="7" r="3.2" />
          <path d="M15.5 15.5l1.6 1.6 3.4-3.6" />
        </svg>
        <span>{{ t("nav_profile") }}</span>
      </NavItem>
      <NavItem view="alerts">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        <span>{{ t("nav_alerts") }}</span>
        <span v-if="notifications.unread" class="nbadge" aria-label="Unread alerts">{{ notifications.unread }}</span>
      </NavItem>
    </nav>
  </aside>
</template>

<script setup>
// FE-5 · nine entries, in the order the work is done: Maya, then the account (Dashboard, Add
// brand), then one brand's own run — Reports, Plan, Sites, Distribution, Files, Profile, Alerts.
//
// Three entries left the rail here and none of them lost its screen. Impact and Fact check are
// Reports tabs (their switches now decide whether the TAB is drawn, in ReportsView), and Pages
// folded into Sites as the comparison matrix. `router.js` redirects all three old paths.

import { useRouter } from "vue-router";

import { shell } from "../shellState";
import { assetUrl } from "../api/client";
import { notifications } from "../stores/notifications";
import { mayaUnread } from "../stores/mayaUnread";  // GEO_EXEC_R19
import { distribution } from "../stores/distribution";  // GEO_EXEC_R14
import { useI18n } from "../useI18n";
import NavItem from "./NavItem.vue";

const { t } = useI18n();
const router = useRouter();
const logoSrc = assetUrl("/assets/img/logo.webp");
const mayaSrc = assetUrl("/assets/img/maya-256.webp");

function addBrand() {
  router.push({ path: "/maya", query: { intent: "add-brand" } }).finally(() => window.dispatchEvent(new CustomEvent("soa:add-brand")));
}
</script>
